import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import AppLayout from "@/shared/components/AppLayout";
import ProtectedRoute from "./hooks/ProtectedRoute";
import {
	getParseOrderSearchDefaultData,
	getPrefixBeforeSlash,
	isProdataPortal,
	setDynamicStyles,
	setPageTitle,
} from "./shared/lib/utils";
import { useWhitelabel } from "./hooks/useWhitelabel";
import ErrorPage from "@/pages/ErrorPage";
import { useMemo } from "react";
// import { browserStorageWatcher } from "@/hooks/CustomLocalStorage";
import LoaderOverlay from "./shared/components/LoaderOverlay";
import { useAppSelector } from "./redux/store";

export default function App() {
	const whitelabel = useWhitelabel();
	const overlay = useAppSelector((store: { overlay: { isOpen: boolean } }) => store.overlay);
	const sessionUser = useAppSelector(store => store.sessionUser);
	const orderSearchDefault = useAppSelector(store =>
		getPrefixBeforeSlash(
			store.sessionUser.order_search_default || (sessionUser.is_admin === "Y" ? "CUSTOM_OPERATIONS" : "GLOBAL"),
		),
	);
	const router = useMemo(
		() =>
			createBrowserRouter([
				{
					path: "login",
					errorElement: <ErrorPage />,
					async lazy() {
						const Login = await import("@/pages/Login");
						return { Component: Login.default };
					},
				},
				{
					path: "unsubscribe-email/:token",
					errorElement: <ErrorPage />,
					async lazy() {
						const Login = await import("@/pages/UnsubscribeEmail");
						return { Component: Login.default };
					},
				},
				{
					path: "terms-conditions",
					errorElement: <ErrorPage />,
					async lazy() {
						if (!isProdataPortal()) return { Component: () => <Navigate to="/" replace /> };
						const Terms = await import("@/pages/Terms");
						return { Component: Terms.default };
					},
				},
				{
					path: "pass-key",
					errorElement: <ErrorPage />,
					async lazy() {
						const PassKey = await import("@/pages/PassKey");
						return {
							Component: () => (
								<ProtectedRoute>
									<PassKey.default />
								</ProtectedRoute>
							),
						};
					},
				},

				{
					path: "privacy-policy",
					errorElement: <ErrorPage />,
					async lazy() {
						if (!isProdataPortal()) return { Component: () => <Navigate to="/" replace /> };
						const PrivacyPolicy = await import("@/pages/PrivacyPolicy");
						return { Component: PrivacyPolicy.default };
					},
				},
				{
					path: "forgot-password",
					errorElement: <ErrorPage />,
					async lazy() {
						const ForgotPassword = await import("@/pages/ForgotPassword");
						return { Component: ForgotPassword.default };
					},
				},
				{
					path: "reset-password",
					errorElement: <ErrorPage />,
					async lazy() {
						const ForgotPassword = await import("@/pages/ResetPassword");
						return { Component: ForgotPassword.default };
					},
				},
				{
					path: "two-factor-authorization",
					errorElement: <ErrorPage />,
					async lazy() {
						const TwoFA = await import("@/pages/TwoFA");
						return {
							Component: () => (
								<ProtectedRoute>
									<TwoFA.default />
								</ProtectedRoute>
							),
						};
					},
				},
				{
					path: "verify-otp",
					errorElement: <ErrorPage />,
					async lazy() {
						const TwoFactorAuthorization = await import("@/pages/TwoFA/TwoFactorAuthorization");
						return { Component: TwoFactorAuthorization.default };
					},
				},

				{
					path: "two-factor-authorization/setup-success",
					errorElement: <ErrorPage />,
					async lazy() {
						const Setup2FASuccess = await import("@/pages/TwoFA/Setup2FASuccess");
						return {
							Component: () => (
								<ProtectedRoute>
									<Setup2FASuccess.default />
								</ProtectedRoute>
							),
						};
					},
				},

				// {
				// 	path: "/assets",
				// 	errorElement: <ErrorPage />,
				// 	element: (
				// 		<>
				// 			<ProtectedRoute>
				// 				<AppLayout isStorageSideBar={true} />
				// 			</ProtectedRoute>
				// 		</>
				// 	),
				// 	children: [
				// 		{
				// 			path: "setup",
				// 			async lazy() {
				// 				const AssetArchive = await import("@/pages/agency-order/AssetArchive");
				// 				return { Component: AssetArchive.default };
				// 			},
				// 		},
				// 	],
				// },
				{
					path: "/preview/email-template/:id",
					async lazy() {
						const Preview = await import("@/pages/admin/pd-marketing/Templates/Preview");
						return {
							Component: () => (
								<ProtectedRoute>
									<Preview.default />
								</ProtectedRoute>
							),
						};
					},
				},
				{
					path: "/",
					errorElement: <ErrorPage />,
					element: (
						<>
							<ProtectedRoute>
								<AppLayout />
							</ProtectedRoute>
						</>
					),
					children: [
						{
							async lazy() {
								const Dashboard = await import("@/pages/Dashboard");
								return { Component: Dashboard.default };
							},
						},
						{
							index: true, // when path is exactly "/"
							element: <Navigate to="/dashboard" replace />,
						},
						{
							path: "dashboard",
							async lazy() {
								const Dashboard = await import("@/pages/Dashboard");
								return { Component: Dashboard.default };
							},
						},
						{
							path: "dooh",
							children: [
								{
									path: "screen-lookup",
									async lazy() {
										const ScreenLookup = await import("@/pages/dooh/ScreenLookup");
										return { Component: ScreenLookup.default };
									},
								},
								{
									path: "screen-lookup/audience/:audienceId?",
									async lazy() {
										const ScreenLookup = await import("@/pages/dooh/ScreenLookup");
										return { Component: ScreenLookup.default };
									},
								},
								{
									path: "audience/list",
									async lazy() {
										const AudienceList = await import("@/pages/dooh/AudienceList");
										return { Component: AudienceList.default };
									},
								},
							],
						},
						{
							path: "agency-order",
							children: [
								{
									path: "order-setup",
									async lazy() {
										const CreateNewOrder = await import("@/pages/agency-order/AgencyOrder");
										return { Component: CreateNewOrder.default };
									},
								},
								{
									path: "order-setup/:order_id",
									async lazy() {
										const UpdateOrder = await import("@/pages/agency-order/AgencyOrder");
										return { Component: UpdateOrder.default };
									},
								},
								{
									path: "reporting/advanced/:pdInternalId",
									async lazy() {
										const RealTimeDigitalSummary =
											await import("@/pages/agency-order/order-listing/real-time-digital-summary/RealTimeDigitalSummary");
										return { Component: RealTimeDigitalSummary.default };
									},
								},
								{
									path: `order-listing/${getParseOrderSearchDefaultData(orderSearchDefault).path}`,
									children: [
										{
											index: true,
											async lazy() {
												const OrderListing = await import("@/pages/agency-order/order-listing/OrderListing");
												return { Component: OrderListing.default };
											},
										},
									],
								},
								{
									path: "renewal_reporting",
									async lazy() {
										const RenewalReporting = await import("@/pages/agency-order/renewal-reporting/RenewalReporting");
										return { Component: RenewalReporting.default };
									},
								},
							],
						},
						{
							path: "eblast",
							children: [
								{
									path: "pd-html-loader/:order_id/:deployment_id/:file_id",
									async lazy() {
										const PdHtmlLoader = await import("@/pages/agency-order/PdHtmlLoader");
										return { Component: PdHtmlLoader.default };
									},
								},
							],
						},
						{
							path: "traffic-estimator",
							children: [
								{
									index: true,
									async lazy() {
										const TrafficEstimator = await import("@/pages/traffic-estimator/TrafficEstimator");
										return { Component: TrafficEstimator.default };
									},
								},
								{
									path: "builder",
									async lazy() {
										const Builder = await import("@/pages/traffic-estimator/builder/Builder");
										return { Component: Builder.default };
									},
								},
								{
									path: "builder/:id",
									async lazy() {
										const Builder = await import("@/pages/traffic-estimator/builder/Builder");
										return { Component: Builder.default };
									},
								},
							],
						},
						{
							path: "billing",
							children: [
								{
									path: "report/:startDate?/:endDate?",
									async lazy() {
										const BillingReport = await import("@/pages/admin/billing-report");
										return { Component: BillingReport.default };
									},
								},
							],
						},

						{
							path: "/ai/audio",
							children: [
								{
									path: "list/:perPage?/:page?/:sortColumn?/:sortDir?",
									async lazy() {
										const Index = await import("@/pages/ai/audio");
										return { Component: Index.default };
									},
								},

								{
									path: "build/:adID?",
									async lazy() {
										const Build = await import("@/pages/ai/audio/build/Build");
										return { Component: Build.default };
									},
								},
							],
						},
						{
							path: "manage",
							children: [
								{
									path: "user",
									children: [
										{
											path: "list/:sortColumn/:sortDir",
											async lazy() {
												const ManageUserList = await import("@/pages/manage/ManageUserList");
												return { Component: ManageUserList.default };
											},
										},
									],
								},
							],
						},
						{
							path: "/admin",
							children: [
								{
									path: "profile",
									async lazy() {
										const UserProfile = await import("@/pages/admin/profile/UserProfile");
										return { Component: UserProfile.default };
									},
								},
								{
									path: "billing/profile",
									async lazy() {
										const BillingInformation = await import("@/pages/admin/billing-information/BillingInformation");
										return { Component: BillingInformation.default };
									},
								},
								{
									path: "campaign-csv/list",
									async lazy() {
										const CampaignCsvExporter = await import("@/pages/admin/campaign-csv-exporter/CampaignCsvExporter");
										return { Component: CampaignCsvExporter.default };
									},
								},
								{
									path: "rtb/networks",
									async lazy() {
										const rtbNetwork = await import("@/pages/admin/rtb-network/RtbNetwork");
										return { Component: rtbNetwork.default };
									},
								},
								{
									path: "campaign-csv/spreadsheet",
									async lazy() {
										const CampaignCSVSpreadsheet =
											await import("@/pages/admin/campaign-csv-spreadsheet/CampaignCSVSpreadsheet");
										return { Component: CampaignCSVSpreadsheet.default };
									},
								},
								{
									path: "campaign-csv/campaigns/:campaignId/review",
									async lazy() {
										const CampaignCsvReview = await import("@/pages/admin/campaign-csv-exporter/CampaignCsvReview");
										return { Component: CampaignCsvReview.default };
									},
								},
								{
									path: "manage",
									children: [
										{
											path: "user",
											children: [
												{
													path: "list/:sortColumn/:sortDir",
													async lazy() {
														const AgencyUsersList =
															await import("@/pages/admin/account-management/user/AdminUsersList");
														return { Component: AgencyUsersList.default };
													},
												},
											],
										},
										{
											path: "agency",
											children: [
												{
													path: "list/:status/:sortColumn/:sortDir",
													async lazy() {
														const AgencyList = await import("@/pages/admin/account-management/agency/AgencyList");
														return { Component: AgencyList.default };
													},
												},
												{
													path: ":id/user/list/:sortColumn/:sortDir",
													async lazy() {
														const AgencyUserList =
															await import("@/pages/admin/account-management/agency/AgencyUserList");
														return { Component: AgencyUserList.default };
													},
												},
												{
													path: ":id/profile",
													async lazy() {
														const AgencyProfile = await import("@/pages/admin/account-management/agency/AgencyProfile");
														return { Component: AgencyProfile.default };
													},
												},
												{
													path: ":id/whitelabel",
													async lazy() {
														const AgencyWhiteLabel =
															await import("@/pages/admin/account-management/agency/AgencyWhiteLabel");
														return { Component: AgencyWhiteLabel.default };
													},
												},
												{
													path: ":id/billing/profile",
													async lazy() {
														const AgencyBilling = await import("@/pages/admin/account-management/agency/AgencyBilling");
														return { Component: AgencyBilling.default };
													},
												},
											],
										},
									],
								},
								{
									path: "email-templates",
									children: [
										{
											path: ":perPage?/:page?/:sortColumn?/:sortDir?",
											async lazy() {
												const emailTemplates = await import("@/pages/admin/pd-marketing/Templates");
												return { Component: emailTemplates.default };
											},
										},
										{
											path: "form/:template_id?",
											async lazy() {
												const emailTemplatesForm = await import("@/pages/admin/pd-marketing/Templates/Form");
												return { Component: emailTemplatesForm.default };
											},
										},
									],
								},
								{
									path: "pd-marketing",
									children: [
										{
											path: "list/:perPage?/:page?/:sortColumn?/:sortDir?/:search?/:status?",
											async lazy() {
												const emailTemplates = await import("@/pages/admin/pd-marketing");
												return { Component: emailTemplates.default };
											},
										},

										{
											path: "form/:template_id?",
											async lazy() {
												const Edit = await import("@/pages/admin/pd-marketing/Form");
												return { Component: Edit.default };
											},
										},
									],
								},
								{
									path: "pd-marketing/sequence",
									children: [
										{
											path: "list/:perPage?/:page?/:sortColumn?/:sortDir?",
											async lazy() {
												const emailTemplates = await import("@/pages/admin/pd-marketing-sequence/List");
												return { Component: emailTemplates.default };
											},
										},
										{
											path: "form/:sequence_id?",
											async lazy() {
												const emailTemplatesForm = await import("@/pages/admin/pd-marketing-sequence/Create");
												return { Component: emailTemplatesForm.default };
											},
										},
									],
								},
								{
									path: "pd-marketing/send-sequence/recipients/:drip_id",
									async lazy() {
										const Recipients = await import("@/pages/admin/pd-marketing-sequence/Send/Recipients");
										return {
											Component: () => (
												<ProtectedRoute>
													<Recipients.default />
												</ProtectedRoute>
											),
										};
									},
								},
								{
									path: "pd-marketing/send-sequence",
									children: [
										{
											path: "list/:perPage?/:page?/:sortColumn?/:sortDir?",
											async lazy() {
												const emailTemplates = await import("@/pages/admin/pd-marketing-sequence/Send/List");
												return { Component: emailTemplates.default };
											},
										},
										{
											path: "form/:drip_id?",
											async lazy() {
												const emailTemplatesForm = await import("@/pages/admin/pd-marketing-sequence/Send/Create");
												return { Component: emailTemplatesForm.default };
											},
										},
									],
								},
								{
									path: "pd-marketing/profile",
									children: [
										{
											path: "list/:perPage?/:page?/:sortColumn?/:sortDir?/:search?/:status?/:clicker?/:startDate?/:endDate?",
											async lazy() {
												const profile = await import("@/pages/admin/pd-marketing-profile");
												return { Component: profile.default };
											},
										},
										{
											path: "form",
											async lazy() {
												const profileForm = await import("@/pages/admin/pd-marketing-profile/Form");
												return { Component: profileForm.default };
											},
										},
										{
											path: "form/:profile_id",
											async lazy() {
												const profileForm = await import("@/pages/admin/pd-marketing-profile/Form/edit");
												return { Component: profileForm.default };
											},
										},
									],
								},
								{
									path: "pd-marketing-list",
									children: [
										{
											path: "list/:perPage?/:page?/:sortColumn?/:sortDir?",
											async lazy() {
												const list = await import("@/pages/admin/pd-marketing-profile-list");
												return { Component: list.default };
											},
										},
										{
											path: "form/:list_id",
											async lazy() {
												const listForm = await import("@/pages/admin/pd-marketing-profile-list/Form");
												return { Component: listForm.default };
											},
										},
									],
								},
							],
						},
						{
							path: "url-parameter-manager",
							async lazy() {
								const UrlManager = await import("@/pages/url-parameter-manager");
								return { Component: UrlManager.default };
							},
						},
					],
				},
			]),
		[orderSearchDefault],
	);

	setPageTitle(whitelabel.company_name);
	setDynamicStyles(whitelabel);

	// useEffect(() => {
	// 	browserStorageWatcher.setupBrowserCloseWatcher(() => {
	// 		console.log("Cleared due to browser close.");
	// 	});
	// }, []);

	return (
		<>
			<LoaderOverlay isOpen={overlay.isOpen} />
			<RouterProvider router={router} />
		</>
	);
}
